import { useCallback, useEffect, useState } from 'react';
import { api, downloadFile, errorText, formatMoney, formatDateTime } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, Empty, Loading, Notice, TextField } from '../components/ui';

interface Schnitt {
  von: string | null;
  bis: string;
  bons: number;
  brutto_cents: number;
  bar_cents: number;
  karte_cents: number;
  sonstige_cents: number;
  einlagen_cents: number;
  entnahmen_cents: number;
  tse_ausstehend: number;
}

interface Kassenbuchzeile {
  zeit: string;
  art: 'verkauf' | 'einlage' | 'entnahme';
  bezeichnung: string;
  betrag_cents: number;
  saldo_cents: number;
}

interface Kassenbuch {
  von: string;
  bis: string;
  anfangsbestand_cents: number;
  endbestand_cents: number;
  zeilen: Kassenbuchzeile[];
}

interface Abschluss extends Schnitt {
  id: string;
  z_nummer: string;
  gezaehlt_cents: number | null;
  differenz_cents: number | null;
  notiz: string | null;
  erstellt_am: string;
}

export function CashClosePage() {
  const { can } = useAuth();
  const darfAbschliessen = can('admin', 'kaufmann', 'schichtleiter');

  const [schnitt, setSchnitt] = useState<Schnitt | null>(null);
  const [historie, setHistorie] = useState<Abschluss[]>([]);
  const [buch, setBuch] = useState<Kassenbuch | null>(null);
  const [gezaehlt, setGezaehlt] = useState('');
  const [notiz, setNotiz] = useState('');
  const [kbArt, setKbArt] = useState<'einlage' | 'entnahme'>('einlage');
  const [kbBetrag, setKbBetrag] = useState('');
  const [kbGrund, setKbGrund] = useState('');
  const [exVon, setExVon] = useState(`${new Date().toISOString().slice(0, 8)}01`);
  const [exBis, setExBis] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const laden = useCallback(async () => {
    setError('');
    try {
      const [s, h, b] = await Promise.all([
        api<Schnitt>('/api/pos/schnitt'),
        api<Abschluss[]>('/api/pos/abschluss'),
        api<Kassenbuch>('/api/pos/kassenbuch'),
      ]);
      setSchnitt(s);
      setHistorie(h);
      setBuch(b);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  async function dsfinvkExport() {
    try {
      await downloadFile(`/api/pos/dsfinvk?von=${exVon}&bis=${exBis}`);
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function kassenbuchBuchen() {
    const cents = Math.round(Number(kbBetrag.replace(',', '.')) * 100);
    if (!(cents > 0) || !kbGrund.trim()) {
      setError('Betrag und Grund werden gebraucht.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api('/api/pos/kassenbuch', {
        method: 'POST',
        body: { art: kbArt, betragCents: cents, grund: kbGrund.trim() },
      });
      setMessage(`${kbArt === 'einlage' ? 'Einlage' : 'Entnahme'} gebucht.`);
      setKbBetrag('');
      setKbGrund('');
      await laden();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void laden();
  }, [laden]);

  async function abschliessen() {
    if (!schnitt || schnitt.bons === 0) return;
    setBusy(true);
    setError('');
    try {
      const g = gezaehlt.trim() ? Math.round(Number(gezaehlt.replace(',', '.')) * 100) : null;
      const z = await api<Abschluss>('/api/pos/abschluss', {
        method: 'POST',
        body: { gezaehltCents: g, notiz: notiz.trim() || null },
      });
      setMessage(`Abschluss ${z.z_nummer} erstellt.`);
      setGezaehlt('');
      setNotiz('');
      await laden();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function nachsignieren() {
    setBusy(true);
    setError('');
    try {
      const r = await api<{ signiert: number; offen: number }>('/api/pos/nachsignieren', { method: 'POST' });
      setMessage(`${r.signiert} Bon(s) nachsigniert, ${r.offen} noch offen.`);
      await laden();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!schnitt) return error ? <Notice kind="error">{error}</Notice> : <Loading />;

  const gezaehltCents = gezaehlt.trim() ? Math.round(Number(gezaehlt.replace(',', '.')) * 100) : null;
  const differenz = gezaehltCents != null ? gezaehltCents - schnitt.bar_cents : null;

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}
      {schnitt.tse_ausstehend > 0 && (
        <Notice kind="error">
          <strong>{schnitt.tse_ausstehend} Bon(s) ohne TSE-Signatur</strong> — die TSE war zeitweise
          nicht erreichbar. Die Signatur wird automatisch nachgeholt.{' '}
          <button className="small" onClick={nachsignieren} disabled={busy}>
            Jetzt nachsignieren
          </button>
        </Notice>
      )}

      <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16 }}>
        <Card title="Kassenschnitt (X)">
          <div className="small muted" style={{ marginBottom: 12 }}>
            Offen seit {schnitt.von ? formatDateTime(schnitt.von) : 'Beginn'}. Ein Schnitt zählt,
            verändert aber nichts — erst der Abschluss schließt die Bons.
          </div>
          <Zeile label="Bons" wert={String(schnitt.bons)} />
          <Zeile label="Karte" wert={formatMoney(schnitt.karte_cents)} />
          {schnitt.sonstige_cents > 0 && <Zeile label="Sonstige" wert={formatMoney(schnitt.sonstige_cents)} />}
          <Zeile label="Umsatz gesamt" wert={formatMoney(schnitt.brutto_cents)} />
          {schnitt.einlagen_cents > 0 && <Zeile label="+ Bareinlagen" wert={formatMoney(schnitt.einlagen_cents)} />}
          {schnitt.entnahmen_cents > 0 && <Zeile label="− Barentnahmen" wert={formatMoney(schnitt.entnahmen_cents)} />}
          <Zeile label="Barsoll" wert={formatMoney(schnitt.bar_cents)} stark />
        </Card>

        <Card title="Tagesabschluss (Z)">
          {schnitt.bons === 0 ? (
            <Empty>Keine offenen Bons — nichts abzuschließen.</Empty>
          ) : !darfAbschliessen ? (
            <Notice kind="info">Nur Schichtleitung und Verwaltung dürfen abschließen.</Notice>
          ) : (
            <>
              <div className="small muted" style={{ marginBottom: 12 }}>
                Schließt alle {schnitt.bons} offenen Bons unter einer Z-Nummer ab. Barsoll{' '}
                <strong>{formatMoney(schnitt.bar_cents)}</strong>.
              </div>
              <TextField
                label="Gezähltes Bargeld (€)"
                value={gezaehlt}
                onChange={setGezaehlt}
                hint="leer lassen, wenn nicht gezählt wird"
              />
              {differenz != null && (
                <div style={{ margin: '8px 0' }}>
                  <Badge kind={differenz === 0 ? 'ok' : 'bad'}>
                    Differenz {differenz > 0 ? '+' : ''}
                    {formatMoney(differenz)}
                  </Badge>
                </div>
              )}
              <TextField label="Notiz" value={notiz} onChange={setNotiz} />
              <button className="primary" style={{ marginTop: 12 }} onClick={abschliessen} disabled={busy}>
                Abschluss erstellen
              </button>
            </>
          )}
        </Card>
      </div>

      <Card title="Abschlüsse" padded={false}>
        {historie.length === 0 ? (
          <Empty>Noch kein Abschluss.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Z-Nummer</th>
                  <th>Zeitpunkt</th>
                  <th className="num">Bons</th>
                  <th className="num">Bar</th>
                  <th className="num">Karte</th>
                  <th className="num">Summe</th>
                  <th className="num">Gezählt</th>
                  <th className="num">Differenz</th>
                </tr>
              </thead>
              <tbody>
                {historie.map((z) => (
                  <tr key={z.id}>
                    <td className="mono">{z.z_nummer}</td>
                    <td className="small">{formatDateTime(z.erstellt_am)}</td>
                    <td className="num">{z.bons}</td>
                    <td className="num">{formatMoney(z.bar_cents)}</td>
                    <td className="num">{formatMoney(z.karte_cents)}</td>
                    <td className="num">{formatMoney(z.brutto_cents)}</td>
                    <td className="num muted">{z.gezaehlt_cents != null ? formatMoney(z.gezaehlt_cents) : '—'}</td>
                    <td className="num">
                      {z.differenz_cents == null ? (
                        '—'
                      ) : (
                        <span style={{ color: z.differenz_cents === 0 ? undefined : 'var(--bad)' }}>
                          {z.differenz_cents > 0 ? '+' : ''}
                          {formatMoney(z.differenz_cents)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Kassenbuch (heute)">
        {darfAbschliessen && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
            <label className="field" style={{ minWidth: 140 }}>
              <span>Art</span>
              <select value={kbArt} onChange={(e) => setKbArt(e.target.value as 'einlage' | 'entnahme')}>
                <option value="einlage">Bareinlage (Geld hinein)</option>
                <option value="entnahme">Barentnahme (Geld heraus)</option>
              </select>
            </label>
            <TextField label="Betrag (€)" value={kbBetrag} onChange={setKbBetrag} />
            <TextField label="Grund" value={kbGrund} onChange={setKbGrund} hint="z. B. Wechselgeld, Bankeinzahlung" />
            <button onClick={kassenbuchBuchen} disabled={busy || !kbBetrag.trim() || !kbGrund.trim()}>
              Buchen
            </button>
          </div>
        )}
        {!buch ? (
          <Loading />
        ) : (
          <>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
              <span className="small muted">
                Anfangsbestand <strong>{formatMoney(buch.anfangsbestand_cents)}</strong>
              </span>
              <span className="small muted">
                Endbestand <strong>{formatMoney(buch.endbestand_cents)}</strong>
              </span>
            </div>
            {buch.zeilen.length === 0 ? (
              <Empty>Heute noch keine Barbewegung.</Empty>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Zeit</th>
                      <th>Vorgang</th>
                      <th className="num">Betrag</th>
                      <th className="num">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buch.zeilen.map((z, i) => (
                      <tr key={i}>
                        <td className="small muted">{formatDateTime(z.zeit)}</td>
                        <td>
                          {z.art === 'einlage' && <Badge kind="ok">Einlage</Badge>}
                          {z.art === 'entnahme' && <Badge kind="warn">Entnahme</Badge>}{' '}
                          {z.bezeichnung}
                        </td>
                        <td className="num" style={{ color: z.betrag_cents < 0 ? 'var(--bad)' : undefined }}>
                          {z.betrag_cents > 0 ? '+' : ''}
                          {formatMoney(z.betrag_cents)}
                        </td>
                        <td className="num mono">{formatMoney(z.saldo_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>

      {darfAbschliessen && (
        <Card title="DSFinV-K-Export (Kassennachschau)">
          <div className="small muted" style={{ marginBottom: 12 }}>
            Die Kassendaten im DSFinV-K-Dateiaufbau als ZIP — Abschlüsse, Bons, Positionen, Steuern,
            Zahlarten und TSE-Daten mit index.xml. Für die Kassennachschau nach § 146b AO. Kernumfang;
            die Liesmich im Archiv erläutert den Rahmen.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <TextField label="von" type="date" value={exVon} onChange={setExVon} />
            <TextField label="bis" type="date" value={exBis} onChange={setExBis} />
            <button className="primary" onClick={dsfinvkExport}>
              DSFinV-K-Export herunterladen
            </button>
          </div>
        </Card>
      )}
    </>
  );
}

function Zeile({ label, wert, stark }: { label: string; wert: string; stark?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '4px 0',
        fontWeight: stark ? 700 : 400,
        fontSize: stark ? '1.15em' : undefined,
      }}
    >
      <span className={stark ? undefined : 'muted'}>{label}</span>
      <span>{wert}</span>
    </div>
  );
}
